// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// The parent component of the Results page, contained by the ResultsContainer.

import React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";

import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import CardDeck from "react-bootstrap/CardDeck";
import FormControl from "react-bootstrap/FormControl";

import { saveAs } from "file-saver";

import ByDivision from "./ByDivision";
import ByPoints from "./ByPoints";
import ErrorModal from "../ErrorModal";

import { mergePlatform } from "../../actions/registrationActions";
import { overwriteStore } from "../../actions/globalActions";
import { buildPlatformMerge, PlatformMergePayload } from "../../wrpf/handoff/mergePlatformData";
import { normalizeGlobalStateDivisions } from "../../wrpf/handoff/normalizeWrpfDivisions";

import { liftingPresentOnPlatform, getWhetherPlatformsHaveLifted } from "../../logic/entry";
import { getString } from "../../logic/strings";
import {
  ALL_DAYS,
  ALL_DIVISIONS,
  ALL_MODALITIES,
  ALL_SEXES,
  divisionsInMeet,
  modalitiesInMeet,
  modalityLabel,
  sexesInMeet,
} from "../../logic/resultsModality";
import { exportAsOplCsv } from "../../logic/export/oplcsv";
import { exportAsUSAPLCsv } from "../../logic/export/usapl";

import { Language, Sex } from "../../types/dataTypes";
import { GlobalState } from "../../types/stateTypes";

import { PublishToWrpfButton } from "../../wrpf/handoff/PublishToWrpfButton";
import { sanitizeStateForOpenlifterFile } from "../../wrpf/handoff/buildExportBundle";
import { checkExhausted, assertString } from "../../types/utils";
import { Dispatch } from "redux";

import styles from "./ResultsView.module.scss";

type ResultsBy = "Division" | "Points" | "BestMastersLifter" | "BestJuniorsLifter";

const assertValidResultsBy = (value: string): value is ResultsBy => {
  const resultsBy = value as ResultsBy;
  switch (resultsBy) {
    case "BestJuniorsLifter":
    case "BestMastersLifter":
    case "Division":
    case "Points":
      return true;
    default:
      checkExhausted(resultsBy);
      throw new Error(`Expected a valid value for ResultsBy. Got "${value}"`);
  }
};

interface StateProps {
  global: GlobalState;
  language: Language;
}

interface DispatchProps {
  mergePlatform: (payload: PlatformMergePayload) => void;
  overwriteStoreState: (state: GlobalState) => void;
}

type Props = StateProps & DispatchProps;

interface InternalState {
  // Filtros aditivos de la premiación ("" = sin filtrar). Ninguno descarta
  // atletas: el viejo filtro por día dejaba afuera a los de otras tandas y armaba
  // podios incompletos. El de ahora elige las categorías que compiten ese día.
  day: string;
  modality: string;
  sex: string;
  division: string;
  by: ResultsBy;
  // Controls the ErrorModal popup. Shown when error !== "".
  error: string;
}

// FIXME: Unfortunate use of globals :/ I don't have a better idea.
// This is to pass information from the merge button click handler to the
// file loader click handler.
let globalMergeDay: number = 0;
let globalMergePlatform: number = 0;

class ResultsView extends React.Component<Props, InternalState> {
  constructor(props: Props) {
    super(props);

    this.handleDayChange = this.handleDayChange.bind(this);
    this.handleModalityChange = this.handleModalityChange.bind(this);
    this.handleSexChange = this.handleSexChange.bind(this);
    this.handleDivisionChange = this.handleDivisionChange.bind(this);
    this.handleByChange = this.handleByChange.bind(this);
    this.handleExportAsOplCsvClick = this.handleExportAsOplCsvClick.bind(this);
    this.handleExportAsUSAPLCsvClick = this.handleExportAsUSAPLCsvClick.bind(this);
    this.handleExportPlatformClick = this.handleExportPlatformClick.bind(this);
    this.handleMergePlatformClick = this.handleMergePlatformClick.bind(this);
    this.handleLoadFileInput = this.handleLoadFileInput.bind(this);
    this.closeErrorModal = this.closeErrorModal.bind(this);
    this.makePlatformMergeButtons = this.makePlatformMergeButtons.bind(this);

    this.state = {
      day: ALL_DAYS,
      modality: ALL_MODALITIES,
      sex: ALL_SEXES,
      division: ALL_DIVISIONS,
      by: "Division",
      error: "",
    };
  }

  makeDayOptions = () => {
    const language = this.props.language;
    const options = [
      <option key={"all"} value={ALL_DAYS}>
        {getString("results.all-days-together", language)}
      </option>,
    ];
    const template = getString("results.just-day-n", language);
    for (let day = 1; day <= this.props.global.meet.lengthDays; day++) {
      options.push(
        <option key={day} value={String(day)}>
          {template.replace("{N}", String(day))}
        </option>,
      );
    }
    return options;
  };

  makeModalityOptions = () => {
    const language = this.props.language;
    const options = [
      <option key={"all"} value={ALL_MODALITIES}>
        {getString("results.all-modalities", language)}
      </option>,
    ];

    const modalities = modalitiesInMeet(this.props.global.registration.entries, this.props.global.meet);
    for (const modality of modalities) {
      options.push(
        <option key={modality.key} value={modality.key}>
          {modalityLabel(modality, language)}
        </option>,
      );
    }
    return options;
  };

  makeSexOptions = () => {
    const language = this.props.language;
    const label = (sex: Sex): string => {
      switch (sex) {
        case "F":
          return getString("results.womens", language);
        case "M":
          return getString("results.mens", language);
        case "Mx":
          return getString("results.mxs", language);
        default:
          checkExhausted(sex);
          return "";
      }
    };

    const options = [
      <option key={"all"} value={ALL_SEXES}>
        {getString("results.all-sexes", language)}
      </option>,
    ];
    for (const sex of sexesInMeet(this.props.global.registration.entries)) {
      options.push(
        <option key={sex} value={sex}>
          {label(sex)}
        </option>,
      );
    }
    return options;
  };

  makeDivisionOptions = () => {
    const options = [
      <option key={"all"} value={ALL_DIVISIONS}>
        {getString("results.all-divisions", this.props.language)}
      </option>,
    ];
    for (const division of divisionsInMeet(this.props.global.registration.entries, this.props.global.meet)) {
      options.push(
        <option key={division} value={division}>
          {division}
        </option>,
      );
    }
    return options;
  };

  handleDayChange = (event: React.BaseSyntheticEvent) => {
    const day = event.currentTarget.value;
    if (this.state.day !== day && assertString(day)) {
      this.setState({ day: day });
    }
  };

  handleModalityChange = (event: React.BaseSyntheticEvent) => {
    const modality = event.currentTarget.value;
    if (this.state.modality !== modality && assertString(modality)) {
      this.setState({ modality: modality });
    }
  };

  handleSexChange = (event: React.BaseSyntheticEvent) => {
    const sex = event.currentTarget.value;
    if (this.state.sex !== sex && assertString(sex)) {
      this.setState({ sex: sex });
    }
  };

  handleDivisionChange = (event: React.BaseSyntheticEvent) => {
    const division = event.currentTarget.value;
    if (this.state.division !== division && assertString(division)) {
      this.setState({ division: division });
    }
  };

  handleByChange = (event: React.BaseSyntheticEvent) => {
    const by = event.currentTarget.value;
    if (this.state.by !== by && assertString(by) && assertValidResultsBy(by)) {
      this.setState({ by: by });
    }
  };

  handleExportAsOplCsvClick = () => {
    // TODO: Share this logic with HomeContainer.
    let meetname = this.props.global.meet.name;
    if (meetname === "") {
      meetname = getString("common.unnamed-filename", this.props.language);
    }
    meetname = meetname.replace(/ /g, "-");

    const csv: string = exportAsOplCsv(this.props.global);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, meetname + ".opl.csv");
  };

  handleExportAsUSAPLCsvClick = () => {
    // TODO: Share this logic with handleExportAsOplCsvClick.
    let meetname = this.props.global.meet.name;
    if (meetname === "") {
      meetname = getString("common.unnamed-filename", this.props.language);
    }
    meetname = meetname.replace(/ /g, "-");

    const csv: string = exportAsUSAPLCsv(this.props.global);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, meetname + ".USAPL.csv");
  };

  handleExportPlatformClick = (day: number, platform: number) => {
    // TODO: Share this logic with handleExportAsOplCsvClick.
    const language = this.props.language;
    let meetname = this.props.global.meet.name;
    if (meetname === "") {
      meetname = getString("common.unnamed-filename", language);
    }
    meetname = meetname.replace(/ /g, "-");

    const template = getString("results.platform-export-filename", language);
    const exportname = template
      .replace("{day}", String(day))
      .replace("{platform}", String(platform))
      .replace("{meetName}", meetname);

    const state = JSON.stringify(sanitizeStateForOpenlifterFile(this.props.global));
    const blob = new Blob([state], { type: "application/json;charset=utf-8" });
    saveAs(blob, exportname + ".export.openlifter");
  };

  // The file input is hidden, and we want to use a button to activate it.
  // This event handler makes a proxy call to the *real* event handler.
  handleMergePlatformClick = (day: number, platform: number) => {
    const loadHelper = document.getElementById("loadhelper");
    if (loadHelper !== null) {
      globalMergeDay = day;
      globalMergePlatform = platform;
      loadHelper.click();
    }
  };

  // Called when a file is selected for merging platform data.
  handleLoadFileInput = () => {
    const loadHelper = document.getElementById("loadhelper");
    if (loadHelper === null || !(loadHelper instanceof HTMLInputElement) || loadHelper.files === null) {
      return;
    }

    // Get the (day, platform) from global state.
    const day: number = globalMergeDay;
    const platform: number = globalMergePlatform;

    // Remember the props in the onload() closure.
    const props = this.props;
    const language = props.language;

    const rememberThis = this;
    const selectedFile = loadHelper.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      let error: string | null = null;

      // If this occurs, we've introduced a bug when initiating the file reader, or the read failed.
      // Add this check as a guard so the typing is narrowed
      if (typeof reader.result !== "string") {
        window.alert(getString("error.internal-error", language));
        return;
      }

      try {
        const obj: GlobalState = JSON.parse(reader.result);

        // stateVersion must match.
        if (obj.versions.stateVersion !== props.global.versions.stateVersion) {
          const e = getString("error.version-mismatch", language);
          error = e
            .replace("{thisVersion}", props.global.versions.stateVersion)
            .replace("{otherVersion}", obj.versions.stateVersion);
        } else if (obj.meet.name !== props.global.meet.name) {
          // The meet name must match, for sanity checking.
          const e = getString("error.meetname-mismatch", language);
          error = e.replace("{thisName}", props.global.meet.name).replace("{otherName}", obj.meet.name);
        } else if (!liftingPresentOnPlatform(obj.registration.entries, day, platform)) {
          // The meet must actually contain data from the given (day, platform).
          const e = getString("error.no-platform-data", language);
          error = e.replace("{day}", String(day)).replace("{platform}", String(platform));
        } else {
          // Sanity checks passed: re-key incoming entries AND their anchors, then merge.
          const wrpfActive = props.global.wrpfSession.tournamentId != null;
          let currentState = props.global;
          if (wrpfActive) {
            // Normaliza también el estado LOCAL antes del merge: si esta PC arrastra bandas Master
            // legacy (bundle pre-reglamento-ARG en localStorage), mergear entries normalizadas
            // sobre entries legacy partiría una división en dos categorías en Resultados.
            const normalized = normalizeGlobalStateDivisions(currentState);
            if (normalized) {
              props.overwriteStoreState(normalized);
              currentState = normalized;
            }
          }
          const incomingAnchors = obj.wrpfSession?.entryAnchors ?? [];
          props.mergePlatform(
            buildPlatformMerge({
              currentEntries: currentState.registration.entries,
              currentAnchors: currentState.wrpfSession.entryAnchors,
              nextEntryId: currentState.registration.nextEntryId,
              day,
              platform,
              incomingEntries: obj.registration.entries,
              incomingAnchors,
              // Con sesión WRPF activa, las divisiones de la otra PC se normalizan al reglamento
              // argentino (evita re-inyectar bandas Master legacy de 4-5 años).
              normalizeDivisions: wrpfActive,
            }),
          );
        }
      } catch (err) {
        error = getString("error.not-json", language);
      }

      if (typeof error === "string") {
        rememberThis.setState({ error: error });
      }
    };
    reader.readAsText(selectedFile);
  };

  closeErrorModal = () => {
    this.setState({ error: "" });
  };

  makePlatformMergeButtons = () => {
    // Array accessed by platformsHaveLifted[day-1][platform-1].
    const platformsHaveLifted: Array<Array<boolean>> = getWhetherPlatformsHaveLifted(
      this.props.global.meet.platformsOnDays,
      this.props.global.registration.entries,
    );

    const language = this.props.language;
    const combineTemplate = getString("results.combine-platforms-header", language);
    const mergeTemplate = getString("results.merge-platform", language);
    const exportTemplate = getString("results.export-platform", language);

    const forms = [];
    const numDays = Math.min(this.props.global.meet.lengthDays, platformsHaveLifted.length);

    for (let i = 0; i < numDays; i++) {
      const liftedOnDay = platformsHaveLifted[i];

      const buttons = [];
      for (let j = 0; j < liftedOnDay.length; j++) {
        const lifted = liftedOnDay[j];
        const variant = lifted === true ? "success" : "warning";
        const marginStyle = j > 0 ? { marginLeft: "14px" } : undefined;

        const actionTemplate = lifted === true ? exportTemplate : mergeTemplate;
        buttons.push(
          <Button
            key={i + "-" + j}
            variant={variant}
            style={marginStyle}
            onClick={() => {
              lifted === true
                ? this.handleExportPlatformClick(i + 1, j + 1)
                : this.handleMergePlatformClick(i + 1, j + 1);
            }}
          >
            {actionTemplate.replace("{day}", String(i + 1)).replace("{platform}", String(j + 1))}
          </Button>,
        );
      }

      forms.push(
        <div key={i}>
          <div>{combineTemplate.replace("{N}", String(i + 1))}</div>
          <div>{buttons}</div>
          {i < platformsHaveLifted.length - 1 ? <br /> : null}
        </div>,
      );
    }

    return forms;
  };

  render() {
    const language = this.props.language;
    // Con un solo día no hay selector, y un día elegido antes no puede quedar filtrando.
    const multiDay = this.props.global.meet.lengthDays > 1;
    const day = multiDay ? this.state.day : ALL_DAYS;
    const filterKey = `${day}|${this.state.modality}|${this.state.sex}|${this.state.division}`;
    let results = null;
    switch (this.state.by) {
      case "Division":
        results = (
          <ByDivision
            key={filterKey}
            day={day}
            modality={this.state.modality}
            sex={this.state.sex}
            division={this.state.division}
          />
        );
        break;
      case "Points":
        results = (
          <ByPoints
            key={filterKey}
            day={day}
            modality={this.state.modality}
            sex={this.state.sex}
            ageCoefficients="None"
            agePointsCategory="BestLifter"
          />
        );
        break;
      case "BestMastersLifter":
        results = (
          <ByPoints
            key={filterKey}
            day={day}
            modality={this.state.modality}
            sex={this.state.sex}
            ageCoefficients={this.props.global.meet.ageCoefficients}
            agePointsCategory="BestMastersLifter"
          />
        );
        break;
      case "BestJuniorsLifter":
        results = (
          <ByPoints
            key={filterKey}
            day={day}
            modality={this.state.modality}
            sex={this.state.sex}
            ageCoefficients={this.props.global.meet.ageCoefficients}
            agePointsCategory="BestJuniorsLifter"
          />
        );
        break;
      default:
        break;
    }

    // Filtros aditivos: día, modalidad, sexo y división. El día solo aparece en
    // torneos de varios días. La división solo tiene sentido en "Por categoría":
    // las categorías de Best Lifter no la tienen.
    const filterSelectors = (
      <>
        {multiDay ? (
          <FormControl
            name="day"
            value={this.state.day}
            as="select"
            onChange={this.handleDayChange}
            className={`custom-select ${styles.dropdown}`}
            style={{ marginRight: "15px" }}
          >
            {this.makeDayOptions()}
          </FormControl>
        ) : null}

        <FormControl
          name="modality"
          value={this.state.modality}
          as="select"
          onChange={this.handleModalityChange}
          className={`custom-select ${styles.dropdown}`}
          style={{ marginRight: "15px" }}
        >
          {this.makeModalityOptions()}
        </FormControl>

        <FormControl
          name="sex"
          value={this.state.sex}
          as="select"
          onChange={this.handleSexChange}
          className={`custom-select ${styles.dropdown}`}
          style={{ marginRight: "15px" }}
        >
          {this.makeSexOptions()}
        </FormControl>

        {this.state.by === "Division" ? (
          <FormControl
            name="division"
            value={this.state.division}
            as="select"
            onChange={this.handleDivisionChange}
            className={`custom-select ${styles.dropdown}`}
            style={{ marginRight: "15px" }}
          >
            {this.makeDivisionOptions()}
          </FormControl>
        ) : null}
      </>
    );

    return (
      <div>
        <ErrorModal
          error={this.state.error}
          title={getString("results.merge-error-title", language)}
          show={this.state.error !== ""}
          close={this.closeErrorModal}
        />

        <CardDeck>
          <Card style={{ marginBottom: "17px" }}>
            <Card.Header>
              <FormattedMessage id="results.merge-platforms-card-header" defaultMessage="Merge Platforms" />
            </Card.Header>
            <Card.Body>
              <div style={{ fontWeight: "bold" }}>
                <FormattedMessage
                  id="results.merge-platforms-warning"
                  defaultMessage="Merging platforms will overwrite data. Please save before merging."
                />
              </div>
              <br />
              {this.makePlatformMergeButtons()}
            </Card.Body>
          </Card>

          <Card style={{ marginBottom: "17px" }}>
            <Card.Header>
              <FormattedMessage id="results.export-results-card-header" defaultMessage="Export Official Results" />
            </Card.Header>
            <Card.Body>
              <Button onClick={this.handleExportAsOplCsvClick}>
                <FormattedMessage
                  id="results.export-openpowerlifting-button"
                  defaultMessage="Export for OpenPowerlifting"
                />
              </Button>

              <Button onClick={this.handleExportAsUSAPLCsvClick} style={{ marginLeft: "14px" }}>
                <FormattedMessage id="results.export-usapl-button" defaultMessage="Export for USAPL" />
              </Button>

              <PublishToWrpfButton />
            </Card.Body>
          </Card>
        </CardDeck>

        <Card>
          <Card.Header>
            <FormattedMessage id="results.results-card-header" defaultMessage="Results For..." />
          </Card.Header>
          <Card.Body className={styles.controlPanel}>
            {filterSelectors}

            <FormControl
              name="by"
              value={this.state.by}
              as="select"
              onChange={this.handleByChange}
              className={`custom-select ${styles.dropdown}`}
            >
              <option value="Division">{getString("results.by-division", language)}</option>
              {this.props.global.meet.ageCoefficients !== "None" ? (
                <option value="BestJuniorsLifter">{getString("results.best-juniors-lifter", language)}</option>
              ) : null}
              {this.props.global.meet.ageCoefficients !== "None" ? (
                <option value="BestMastersLifter">{getString("results.best-masters-lifter", language)}</option>
              ) : null}
              <option value="Points">{getString("results.best-lifter", language)}</option>
            </FormControl>
          </Card.Body>
        </Card>

        {results}

        <input
          id="loadhelper"
          type="file"
          accept=".openlifter,.openlifter.txt"
          style={{ display: "none" }}
          onChange={this.handleLoadFileInput}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: GlobalState): StateProps => {
  return {
    global: state,
    language: state.language,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    mergePlatform: (payload) => dispatch(mergePlatform(payload)),
    overwriteStoreState: (state) => dispatch(overwriteStore(state)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResultsView);
